import React from "react";

import { triggerById, stepDefById } from "../catalog";
import { SelectControl, PeopleMultiSelect, ChannelChips, ChannelMessages } from "./SetupControls";
import { IconPointer } from "./icons";

export default function SetupPanel({
  selected,
  draft,
  updateConfig,
  openSelect,
  setOpenSelect,
  iconClass,
  kpiOptions = [],
  badgeOptions = [],
  personOptions = [],
  issues = [],
  workspaceName = "",
  membersError = false,
  onRetryMembers,
}) {
  if (!selected) {
    return (
      <div className="gmba-panel">
        <div className="gmba-pempty">
          <IconPointer />
          Pick a step on the canvas to set it up.
        </div>
      </div>
    );
  }

  const isTrigger = selected === "trigger";
  const step = isTrigger ? null : draft.steps.find((s) => s.id === selected);
  const def = isTrigger ? triggerById(draft.trigger?.type) : step && stepDefById(step.type);
  if (!def) {
    return (
      <div className="gmba-panel">
        <div className="gmba-pempty">
          <IconPointer />
          Pick a step on the canvas to set it up.
        </div>
      </div>
    );
  }
  const config = (isTrigger ? draft.trigger.config : step.config) || {};
  const fields = (def.fields || []).filter((f) => !f.showIf || f.showIf(config));

  // Soft warning on notify steps: which explicitly-picked recipients have no
  // mobile number, so SMS won't reach them. Only names people we can resolve
  // to a real record — a fixed target like "the player" or "everyone" can't be
  // checked up front. `hasPhone === false` only (undefined = stale/unknown).
  const smsSelected =
    Array.isArray(config.channels) && config.channels.includes("SMS");
  const smsUnreachable = (() => {
    if (!smsSelected) return [];
    const ids = new Set();
    if (def.recipientPicker && config.recipientId) ids.add(config.recipientId);
    if (def.managerPicker && config.managerId) ids.add(config.managerId);
    if (def.extraRecipients && Array.isArray(config.extraRecipientIds)) {
      config.extraRecipientIds.forEach((id) => ids.add(id));
    }
    const pool = personOptions;
    return [...ids]
      .map((id) => pool.find((o) => o.value === id))
      .filter((o) => o && o.hasPhone === false)
      .map((o) => o.label);
  })();

  // Group visible fields into their sections, keeping catalog order.
  const sections = [];
  fields.forEach((f) => {
    const name = f.section || "Setup";
    let sec = sections.find((s) => s.name === name);
    if (!sec) {
      sec = { name, fields: [] };
      sections.push(sec);
    }
    sec.fields.push(f);
  });

  const renderField = (f) => {
    const keyPath = `${isTrigger ? "trigger" : step.id}.${f.key}`;
    if (f.type === "select") {
      return (
        <SelectControl
          value={config[f.key] ?? f.options[0]}
          options={f.options}
          openKey={keyPath}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          onChange={(v) => updateConfig(selected, f.key, v)}
        />
      );
    }
    if (f.type === "kpi") {
      const options = [
        { value: "", label: kpiOptions.length ? "Pick a metric…" : "No metrics in this workspace" },
        ...kpiOptions,
      ];
      // Keep a stale saved name visible if the metric was retired from the list.
      if (config.kpiId && config.kpiName && !kpiOptions.some((o) => o.value === config.kpiId)) {
        options.splice(1, 0, { value: config.kpiId, label: `${config.kpiName} (saved)` });
      }
      return (
        <SelectControl
          value={config.kpiId || ""}
          options={options}
          openKey={keyPath}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          onChange={(v) => {
            const match = kpiOptions.find((o) => o.value === v);
            updateConfig(selected, {
              kpiId: v || "",
              kpiName: match?.label || "",
            });
          }}
        />
      );
    }
    if (f.type === "badge") {
      const options = [
        { value: "", label: badgeOptions.length ? "Pick a badge…" : "No badges in this company yet" },
        ...badgeOptions,
      ];
      if (config.badgeId && config.badge && !badgeOptions.some((o) => o.value === config.badgeId)) {
        options.splice(1, 0, { value: config.badgeId, label: `${config.badge} (saved)` });
      }
      return (
        <SelectControl
          value={config.badgeId || ""}
          options={options}
          openKey={keyPath}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          onChange={(v) => {
            const match = badgeOptions.find((o) => o.value === v);
            updateConfig(selected, {
              badgeId: v || "",
              badge: match?.label || "",
            });
          }}
        />
      );
    }
    if (f.type === "channels") {
      return (
        <>
          <ChannelChips
            value={config[f.key]}
            options={f.options}
            onChange={(v) => updateConfig(selected, f.key, v)}
          />
          {smsUnreachable.length > 0 && (
            <div className="gmba-smswarn">
              {smsUnreachable.length === 1
                ? `${smsUnreachable[0]} has no mobile number on file — they won't get the SMS.`
                : `${smsUnreachable.join(", ")} have no mobile number on file — they won't get the SMS.`}{" "}
              Add one in Player Management. Any other channels you pick still reach them.
            </div>
          )}
        </>
      );
    }
    if (f.type === "message") {
      return (
        <ChannelMessages
          channels={config.channels}
          message={config.message}
          messages={config.messages}
          placeholder={f.placeholder}
          onMessage={(v) => updateConfig(selected, "message", v)}
          onMessages={(v) => updateConfig(selected, "messages", v)}
          workspaceName={workspaceName}
        />
      );
    }
    return (
      <input
        className="gmba-textin"
        value={config[f.key] || ""}
        placeholder={f.placeholder}
        inputMode={f.key === "amount" ? "decimal" : undefined}
        onChange={(e) => updateConfig(selected, f.key, e.target.value)}
      />
    );
  };

  const issueFor = (field) => issues.find((i) => i.target === selected && i.field === field);

  return (
    <div className="gmba-panel">
      <div className="gmba-phead">
        <span className={`gmba-ic ${iconClass}`} style={{ width: 30, height: 30, flex: "0 0 30px", borderRadius: 8 }}>
          {def.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gmba-pt">{(isTrigger ? config.customLabel : step.label) || def.label}</div>
          <div className="gmba-psub">Set up this step</div>
        </div>
      </div>
      <div className="gmba-pbody">
        {(def.recipient || def.recipientPicker) && (
          <>
            <div className="gmba-sec gmba-first">Recipient</div>
            {membersError && (def.recipientPicker || def.managerPicker || def.extraRecipients) && (
              <div className="gmba-errorbar">
                Couldn't load this workspace's people.
                {onRetryMembers && <button type="button" onClick={onRetryMembers}>Retry</button>}
              </div>
            )}
            <div className={`gmba-fld${issueFor("recipientId") ? " gmba-fld-invalid" : ""}`}>
              <span className="gmba-lbl">Send to</span>
              {def.recipientPicker && (() => {
                const options = [
                  { value: "", label: personOptions.length ? "Pick a person…" : "No one in this workspace yet" },
                  ...personOptions,
                ];
                // Keep a stale saved name visible if that person was later
                // archived/removed entirely — same pattern as the kpi/badge
                // pickers below, so the recipient never silently disappears.
                if (config.recipientId && config.recipientName && !personOptions.some((o) => o.value === config.recipientId)) {
                  options.splice(1, 0, { value: config.recipientId, label: `${config.recipientName} (no longer in workspace)` });
                }
                return (
                  <SelectControl
                    value={config.recipientId || ""}
                    options={options}
                    openKey={`${selected}.recipientId`}
                    searchable
                    openSelect={openSelect}
                    setOpenSelect={setOpenSelect}
                    onChange={(v) => {
                      const match = personOptions.find((o) => o.value === v);
                      updateConfig(selected, { recipientId: v || "", recipientName: match?.label || "" });
                    }}
                  />
                );
              })()}
              {def.managerPicker && (() => {
                // Managers are the company's admins (any workspace) — regular
                // players are never offered here.
                const managerOptions = personOptions.filter((o) => o.isAdmin);
                const options = [
                  { value: "", label: "All managers (default)" },
                  ...managerOptions,
                ];
                if (config.managerId && config.managerName && !managerOptions.some((o) => o.value === config.managerId)) {
                  options.splice(1, 0, { value: config.managerId, label: `${config.managerName} (no longer an admin)` });
                }
                return (
                  <SelectControl
                    value={config.managerId || ""}
                    options={options}
                    openKey={`${selected}.managerId`}
                    searchable
                    openSelect={openSelect}
                    setOpenSelect={setOpenSelect}
                    onChange={(v) => {
                      const match = managerOptions.find((o) => o.value === v);
                      updateConfig(selected, { managerId: v || "", managerName: match?.label || "" });
                    }}
                  />
                );
              })()}
              {!def.recipientPicker && !def.managerPicker && (
                <div className="gmba-chips">
                  <span className="gmba-chipfix">{def.recipient}</span>
                </div>
              )}
              {issueFor("recipientId") && <div className="gmba-fielderror">{issueFor("recipientId").message}</div>}
              {def.recipientHint && <div className="gmba-hint">{def.recipientHint}</div>}
              {def.extraRecipients && (
                <div style={{ marginTop: 10 }}>
                  <PeopleMultiSelect
                    value={config.extraRecipientIds}
                    options={personOptions}
                    openKey={`${selected}.extraRecipientIds`}
                    openSelect={openSelect}
                    setOpenSelect={setOpenSelect}
                    onChange={(ids) => updateConfig(selected, "extraRecipientIds", ids)}
                  />
                  <div className="gmba-hint">Optional — reaches these people too, on top of {(def.recipient || "the pick above").toLowerCase()}.</div>
                </div>
              )}
            </div>
          </>
        )}
        {sections.map((sec, si) => (
          <React.Fragment key={sec.name}>
            <div className={`gmba-sec${!(def.recipient || def.recipientPicker) && si === 0 ? " gmba-first" : ""}`}>{sec.name}</div>
            {sec.fields.map((f) => {
              const issue = issueFor(f.key);
              return (
                <div className={`gmba-fld${issue ? " gmba-fld-invalid" : ""}`} key={f.key}>
                  {f.type !== "message" && <span className="gmba-lbl">{f.label}</span>}
                  {renderField(f)}
                  {issue && <div className="gmba-fielderror">{issue.message}</div>}
                  {!issue && f.hint && f.type !== "message" && <div className="gmba-hint">{f.hint}</div>}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {isTrigger && def.english && (
          <div className="gmba-tip">
            In plain English: <b>{def.english(config)}</b>
          </div>
        )}
        {!fields.length && !def.recipient && !def.recipientPicker && (
          <div className="gmba-tip" style={{ marginTop: 14 }}>
            Nothing to set up — this step just works.
          </div>
        )}
      </div>
    </div>
  );
}
